import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../../api/client';
import { isProtectedStudent } from '../../config/protectedAccount';

const LIMIT = 20;

export default function AdminUsers() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [updating, setUpdating] = useState(null); // user id being updated
  const [accessModal, setAccessModal] = useState(null); // { user, days }
  const [accessDays, setAccessDays] = useState('7');
  const [accessType, setAccessType] = useState('trial'); // 'trial' | 'subscription'

  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-users', page, search],
    queryFn: async () => {
      const result = await adminAPI.getUsers({ page, limit: LIMIT, search });
      if (!result.success) throw new Error(result.error || '사용자를 불러올 수 없습니다');
      return result;
    },
  });

  const users = data?.data || data?.users || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const toggleRoleMutation = useMutation({
    mutationFn: ({ id, role }) => adminAPI.updateUser(id, { role }),
    onSuccess: (_, { id, role }) => {
      queryClient.setQueryData(['admin-users', page, search], (old) => {
        if (!old) return old;
        const updated = (old.data || old.users || []).map((u) =>
          u.id === id ? { ...u, role } : u
        );
        return old.data ? { ...old, data: updated } : { ...old, users: updated };
      });
    },
    onError: (err) => {
      alert(err.response?.data?.error || '업데이트 실패');
    },
    onSettled: () => {
      setUpdating(null);
    },
  });

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleToggleRole = (user) => {
    const newRole = user.role === 'admin' ? 'user' : 'admin';
    setUpdating(user.id);
    toggleRoleMutation.mutate({ id: user.id, role: newRole });
  };

  const grantAccessMutation = useMutation({
    mutationFn: ({ id, access_until, subscription_status }) =>
      adminAPI.updateUser(id, { access_until, subscription_status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setAccessModal(null);
    },
    onError: (err) => alert(err.response?.data?.error || '업데이트 실패'),
  });

  const handleGrantAccess = (user) => {
    setAccessDays('7');
    setAccessType(user.subscription_status === 'active' ? 'subscription' : 'trial');
    setAccessModal(user);
  };

  const submitGrantAccess = () => {
    if (!accessModal) return;
    const days = parseInt(accessDays);
    if (!days || days <= 0) return;
    const current = accessModal.access_until ? new Date(accessModal.access_until) : new Date();
    const base = current > new Date() ? current : new Date();
    const newDate = new Date(base.getTime() + days * 86400000);
    grantAccessMutation.mutate({
      id: accessModal.id,
      access_until: newDate.toISOString(),
      subscription_status: accessType === 'subscription' ? 'active' : null,
    });
  };

  const handleRevokeAccess = (user) => {
    if (!confirm(`${user.student_id} 접근 권한을 즉시 만료시킬까요?`)) return;
    grantAccessMutation.mutate({
      id: user.id,
      access_until: new Date().toISOString(),
      subscription_status: null,
    });
  };

  const getAccessBadge = (user) => {
    const active = user.access_until && new Date(user.access_until) > new Date();
    const days = active ? Math.ceil((new Date(user.access_until) - new Date()) / 86400000) : 0;
    const ss = user.subscription_status;
    if (ss === 'active' && active) return { label: `구독 중 · ${days}일`, cls: 'bg-emerald-100 text-emerald-700' };
    if (ss === 'cancelled' && active) return { label: `취소됨 · ${days}일`, cls: 'bg-amber-100 text-amber-700' };
    if (active) return { label: `체험 · ${days}일`, cls: 'bg-tayo-light text-tayo' };
    return { label: '미구독', cls: 'bg-gray-100 text-gray-500' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ko-KR');
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-charcoal">사용자 관리</h2>
        <p className="text-sm text-sub-text mt-1">전체 사용자 조회 및 관리</p>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="학번 또는 이름으로 검색"
          className="flex-1 px-4 py-2 rounded-xl border border-border text-sm text-charcoal placeholder:text-sub-text focus:outline-none focus:ring-2 focus:ring-tayo/30 bg-white"
        />
        <button
          type="submit"
          className="px-5 py-2 rounded-xl bg-tayo text-white text-sm font-medium hover:bg-tayo-dark transition-colors"
        >
          검색
        </button>
        {search && (
          <button
            type="button"
            onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}
            className="px-4 py-2 rounded-xl bg-gray-100 text-charcoal text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            초기화
          </button>
        )}
      </form>

      {/* Error */}
      {error && (
        <div className="bg-coral/10 border border-coral/20 rounded-xl p-4">
          <p className="text-sm text-coral">{error.message}</p>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-tayo" />
          <p className="mt-2 text-sub-text">로딩 중...</p>
        </div>
      )}

      {/* Table */}
      {!isLoading && (
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          {users.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-sub-text">데이터가 없습니다</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-snow">
                    <tr>
                      {['학번', '이름', '구독', '역할', '가입일', '액션'].map((h) => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-medium text-charcoal uppercase tracking-wider whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-border">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-snow transition-colors">
                        <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-charcoal">
                          {user.student_id || '-'}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-sm text-charcoal">
                          {user.name || '-'}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          {(() => {
                            const badge = getAccessBadge(user);
                            return (
                              <span className={`inline-flex text-xs font-semibold rounded-full px-2.5 py-0.5 ${badge.cls}`}>
                                {badge.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          <span className={`inline-flex text-xs font-semibold rounded-full px-2.5 py-0.5 ${
                            user.role === 'admin'
                              ? 'bg-violet-100 text-violet-700'
                              : 'bg-tayo-light text-tayo'
                          }`}>
                            {user.role === 'admin' ? '관리자' : '사용자'}
                          </span>
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-sm text-sub-text">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          <div className="flex gap-1.5">
                            {!isProtectedStudent(user.student_id) && (
                              <button
                                onClick={() =>
                                  navigate(
                                    `/admin/reserve?userId=${user.id}&studentId=${encodeURIComponent(user.student_id || '')}&name=${encodeURIComponent(user.name || '')}`
                                  )
                                }
                                className="px-2.5 py-1 text-xs font-medium rounded-lg bg-tayo text-white hover:bg-tayo-dark transition-colors"
                              >
                                대신 예약
                              </button>
                            )}
                            <button
                              onClick={() => handleGrantAccess(user)}
                              className="px-2.5 py-1 text-xs font-medium rounded-lg bg-tayo-light text-tayo hover:bg-tayo/20 transition-colors"
                            >
                              +체험
                            </button>
                            {user.access_until && new Date(user.access_until) > new Date() && (
                              <button
                                onClick={() => handleRevokeAccess(user)}
                                className="px-2.5 py-1 text-xs font-medium rounded-lg bg-coral/10 text-coral hover:bg-coral/20 transition-colors"
                              >
                                만료
                              </button>
                            )}
                            {isProtectedStudent(user.student_id) ? (
                              <span className="px-2.5 py-1 text-xs font-medium rounded-lg bg-violet-50 text-violet-400 border border-violet-100">
                                🔒 보호 계정
                              </span>
                            ) : (
                              <button
                                onClick={() => handleToggleRole(user)}
                                disabled={updating === user.id}
                                className="px-2.5 py-1 text-xs font-medium rounded-lg border border-border hover:bg-snow transition-colors disabled:opacity-50"
                              >
                                {user.role === 'admin' ? '→유저' : '→관리자'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="bg-white px-5 py-3 border-t border-border flex items-center justify-between">
                <p className="text-sm text-sub-text">
                  <span className="font-medium text-charcoal">{total}</span>명 중{' '}
                  <span className="font-medium text-charcoal">{(page - 1) * LIMIT + 1}</span>
                  {' - '}
                  <span className="font-medium text-charcoal">{Math.min(page * LIMIT, total)}</span>
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 text-sm font-medium text-charcoal bg-gray-100 rounded-full hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    이전
                  </button>
                  <span className="text-sm text-sub-text">{page} / {totalPages}</span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 text-sm font-medium text-white bg-tayo rounded-full hover:bg-tayo-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    다음
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {/* 체험 부여 모달 */}
      {accessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setAccessModal(null)} />
          <div className="relative bg-white rounded-2xl p-6 w-80 shadow-xl">
            <h3 className="text-lg font-bold text-charcoal mb-1">접근 권한 부여</h3>
            <p className="text-sm text-sub-text mb-4">{accessModal.student_id} {accessModal.name || ''}</p>
            {accessModal.access_until && new Date(accessModal.access_until) > new Date() && (
              <p className="text-xs text-tayo bg-tayo-light rounded-lg px-3 py-2 mb-3">
                현재 {Math.ceil((new Date(accessModal.access_until) - new Date()) / 86400000)}일 남음 — 추가 연장됩니다
              </p>
            )}
            <div className="flex items-center gap-2 mb-4">
              <input
                type="number"
                value={accessDays}
                onChange={(e) => setAccessDays(e.target.value)}
                min="1"
                max="365"
                className="flex-1 px-3 py-2 border border-border rounded-xl text-sm text-center font-bold focus:outline-none focus:ring-2 focus:ring-tayo/30"
              />
              <span className="text-sm text-sub-text">일</span>
            </div>
            <div className="flex gap-2 mb-4">
              {[7, 14, 30].map((d) => (
                <button
                  key={d}
                  onClick={() => setAccessDays(String(d))}
                  className={`flex-1 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                    accessDays === String(d) ? 'border-tayo bg-tayo-light text-tayo' : 'border-border text-sub-text hover:bg-snow'
                  }`}
                >
                  {d}일
                </button>
              ))}
            </div>
            {/* 표시 타입 선택 */}
            <div className="mb-1">
              <p className="text-xs text-sub-text mb-2">표시 타입</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setAccessType('trial')}
                  className={`flex-1 py-2 text-xs font-semibold rounded-lg border-2 transition-colors ${
                    accessType === 'trial' ? 'border-tayo bg-tayo-light text-tayo' : 'border-border text-sub-text hover:bg-snow'
                  }`}
                >
                  체험
                </button>
                <button
                  onClick={() => setAccessType('subscription')}
                  className={`flex-1 py-2 text-xs font-semibold rounded-lg border-2 transition-colors ${
                    accessType === 'subscription' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-border text-sub-text hover:bg-snow'
                  }`}
                >
                  구독
                </button>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setAccessModal(null)}
                className="flex-1 py-2.5 text-sm font-medium rounded-xl border border-border text-sub-text hover:bg-snow transition-colors"
              >
                취소
              </button>
              <button
                onClick={submitGrantAccess}
                disabled={grantAccessMutation.isPending}
                className="flex-1 py-2.5 text-sm font-bold rounded-xl bg-tayo text-white hover:bg-tayo-dark transition-colors disabled:opacity-50"
              >
                {grantAccessMutation.isPending ? '처리 중...' : '부여'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
